import React from "react";
import LegalLayout from "./LegalLayout";
import { BRAND } from "../../lib/brand";

const E = BRAND.legal.entity;
const APP = BRAND.name;
const EMAIL = BRAND.legal.contactEmail;

const SECTIONS = [
    {
        id: "acceptance",
        title: "Acceptance of these Terms",
        body: (
            <>
                <p>
                    These Terms of Service (the &ldquo;<strong>Terms</strong>&rdquo;) form a binding contract between you and {E}
                    (&ldquo;<strong>{APP}</strong>,&rdquo; &ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>,&rdquo; or
                    &ldquo;<strong>our</strong>&rdquo;) and govern your access to and use of our websites, mobile applications, APIs,
                    and related services (collectively, the &ldquo;<strong>Service</strong>&rdquo;).
                </p>
                <p>
                    By creating an account, accessing, or using the Service, you agree to these Terms, our{" "}
                    <a href="/privacy">Privacy Policy</a>, and our{" "}
                    <a href="/community-guidelines">Community Guidelines</a>. If you do not agree, do not use the Service.
                </p>
            </>
        ),
    },
    {
        id: "eligibility",
        title: "Eligibility (18+ only)",
        body: (
            <>
                <p>
                    The Service is intended for adults. You must be <strong>at least 18 years old</strong> and have the legal
                    capacity to enter into a binding contract under the laws of your jurisdiction to register, post content, or
                    purchase a subscription.
                </p>
                <p>
                    We do not knowingly collect information from anyone under 18. If we learn an account belongs to a minor, we
                    will terminate it. If you believe a minor has provided us information, contact{" "}
                    <a href={`mailto:${EMAIL}`}>{EMAIL}</a> and we will delete it.
                </p>
            </>
        ),
    },
    {
        id: "account",
        title: "Your account",
        body: (
            <>
                <p>You are responsible for:</p>
                <ul>
                    <li>The accuracy of the registration information you provide.</li>
                    <li>Keeping your password confidential and your account credentials secure.</li>
                    <li>All activity that occurs under your account, whether authorized by you or not.</li>
                </ul>
                <p>
                    Notify us immediately at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> of any unauthorized use of your account or
                    other security breach.
                </p>
            </>
        ),
    },
    {
        id: "subscription",
        title: "Subscriptions, billing, and refunds",
        body: (
            <>
                <p>
                    {APP} offers a <strong>Free</strong> tier and paid <strong>Adventure</strong> ($5/month) and{" "}
                    <strong>Premium</strong> ($10/month) tiers (collectively, &ldquo;<strong>Subscriptions</strong>&rdquo;).
                    Subscription features and pricing are described on our pricing page and may be updated from time to time;
                    material changes affecting active Subscriptions will be communicated by email at least 14 days in advance.
                </p>
                <h3>Auto-renewal</h3>
                <p>
                    Subscriptions renew automatically each billing period until cancelled. You authorize us (and our payment
                    processor) to charge your selected payment method at the start of each renewal.
                </p>
                <h3>Cancellation</h3>
                <p>
                    You may cancel at any time from <em>Settings → Billing</em>. Cancellation takes effect at the end of the
                    current billing period; you retain access until then.
                </p>
                <h3>Refunds</h3>
                <p>
                    Subscription fees are <strong>non-refundable</strong> except where required by applicable law (including the
                    14-day right of withdrawal under EU consumer law for digital services where you have not yet started use).
                </p>
                <h3>Taxes</h3>
                <p>
                    Listed prices are exclusive of taxes. You are responsible for any sales, use, value-added, or similar taxes
                    that apply.
                </p>
            </>
        ),
    },
    {
        id: "ugc",
        title: "User content and license to us",
        body: (
            <>
                <p>
                    You retain all ownership rights in the photos, videos, GPS routes, text, reviews, and other content you post
                    (&ldquo;<strong>Your Content</strong>&rdquo;).
                </p>
                <p>
                    You grant {E} a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to host,
                    store, reproduce, modify (e.g., resize for performance), publicly display, distribute, and make derivative
                    works of Your Content <strong>solely for the purpose of operating, improving, and promoting the Service</strong>.
                    This license ends when you delete Your Content, except for (a) backup copies retained as required by law and
                    (b) sublicenses already granted to other users (e.g., a photo embedded in someone else&rsquo;s saved adventure).
                </p>
                <p>
                    You represent that you own or have the necessary rights to Your Content and that posting it does not violate
                    any third-party rights or applicable law.
                </p>
            </>
        ),
    },
    {
        id: "acceptable-use",
        title: "Acceptable use",
        body: (
            <>
                <p>You agree not to:</p>
                <ul>
                    <li>Violate any law, regulation, or third-party right.</li>
                    <li>Post content that is illegal, harassing, defamatory, hateful, threatening, sexually explicit, or that depicts violence against people or wildlife.</li>
                    <li>Provide GPS routes or trip details that disclose protected, sacred, or sensitive locations (see our <a href="/community-guidelines">Community Guidelines</a>).</li>
                    <li>Impersonate any person or misrepresent your affiliation with anyone.</li>
                    <li>Scrape, crawl, or harvest data except via our official APIs and within published rate limits.</li>
                    <li>Reverse engineer, decompile, or attempt to extract source code, except as expressly permitted by law.</li>
                    <li>Interfere with the integrity, security, or performance of the Service or any user&rsquo;s account.</li>
                    <li>Use the Service to send unsolicited commercial communications.</li>
                </ul>
                <p>
                    We may remove content, suspend accounts, or terminate access at our discretion when we believe a violation
                    has occurred. Your <a href="/community-guidelines">Community Guidelines</a> describe enforcement in more
                    detail.
                </p>
            </>
        ),
    },
    {
        id: "marketplace",
        title: "Marketplace",
        body: (
            <>
                <p>
                    {APP} provides a venue where Premium members may list, buy, sell, and trade outdoor gear with other members
                    (the &ldquo;<strong>Marketplace</strong>&rdquo;).
                </p>
                <p>
                    {APP} <strong>is not a party</strong> to any marketplace transaction, does not take title to any goods, and
                    does not act as a broker, auctioneer, or escrow agent. You are solely responsible for the legality, accuracy,
                    quality, safety, and delivery of items you offer or buy.
                </p>
                <p>
                    Listings of firearms, ammunition, hunting weapons, and any item subject to government licensing must comply
                    with all federal, state, and local laws including FFL transfer requirements. Items prohibited by these Terms
                    or our Community Guidelines may not be listed.
                </p>
            </>
        ),
    },
    {
        id: "outdoor-risk",
        title: "Assumption of risk for outdoor activities",
        body: (
            <>
                <p>
                    <strong>OUTDOOR ACTIVITIES ARE INHERENTLY DANGEROUS.</strong> Hiking, climbing, backpacking, paddling,
                    skiing, mountaineering, hunting, fishing, off-roading, and similar pursuits involve real risks of personal
                    injury, property damage, and death from causes including but not limited to weather, terrain, wildlife,
                    avalanches, falling rocks, drowning, hypothermia, altitude, equipment failure, navigation error, and the
                    actions of yourself or others.
                </p>
                <p>
                    Adventures, GPS routes, photos, descriptions, gear lists, and trip notes posted on the Service are
                    <strong> user-generated content</strong>. They are <strong>not</strong> verified by {APP}, are <strong>not</strong>{" "}
                    professional guidance, and may be inaccurate, outdated, or misleading. Conditions on any route can change
                    without notice.
                </p>
                <p>
                    By using the Service you acknowledge and agree that:
                </p>
                <ul>
                    <li>You are solely responsible for assessing your own skills, fitness, equipment, and the current conditions before attempting any activity.</li>
                    <li>You assume all risk of loss, injury, or death arising from any activity you attempt based on information found on the Service.</li>
                    <li>You will obey all laws, land-use rules, permits, and posted signage; secure your own permits where required; and respect Leave No Trace principles.</li>
                    <li>{APP} is <strong>not</strong> a search-and-rescue service. In an emergency, call local emergency services.</li>
                </ul>
                <p>
                    To the fullest extent permitted by law, you <strong>release, waive, and forever discharge</strong> {E}, its
                    affiliates, officers, employees, and contractors from any and all claims, liabilities, demands, causes of
                    action, costs, and expenses arising from your participation in any outdoor activity informed by the Service.
                </p>
            </>
        ),
    },
    {
        id: "third-party",
        title: "Third-party services and links",
        body: (
            <>
                <p>
                    The Service may include links to, or integrations with, third-party services (for example, mapping providers,
                    payment processors, push-notification providers, or partner brands). We do not control these services and are
                    not responsible for their content, terms, or privacy practices. Your interactions with third-party services
                    are governed by those parties&rsquo; terms.
                </p>
            </>
        ),
    },
    {
        id: "ip",
        title: "Our intellectual property",
        body: (
            <>
                <p>
                    The Service, including the {APP} name, logo, design, source code, databases, and aggregated content (other
                    than Your Content and other users&rsquo; content), is owned by {E} and protected by copyright, trademark,
                    and other intellectual-property laws. We grant you a limited, non-transferable, revocable license to access
                    and use the Service for its intended personal or internal-business purpose, subject to these Terms.
                </p>
            </>
        ),
    },
    {
        id: "dmca",
        title: "Copyright (DMCA) policy",
        body: (
            <>
                <p>
                    We respond to valid notices of alleged copyright infringement under the U.S. Digital Millennium Copyright
                    Act (17 U.S.C. § 512). If you believe content on the Service infringes your copyright, send a written notice
                    to our designated agent at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> containing:
                </p>
                <ol>
                    <li>Your physical or electronic signature.</li>
                    <li>Identification of the copyrighted work claimed to be infringed.</li>
                    <li>Identification of the material that is claimed to be infringing and its URL on the Service.</li>
                    <li>Your contact information (address, phone, email).</li>
                    <li>A statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent, or the law.</li>
                    <li>A statement, under penalty of perjury, that the information in the notice is accurate and that you are the owner or authorized to act on behalf of the owner of an exclusive right that is allegedly infringed.</li>
                </ol>
                <p>
                    We will remove infringing material in accordance with the DMCA. Repeat infringers&rsquo; accounts will be
                    terminated.
                </p>
            </>
        ),
    },
    {
        id: "termination",
        title: "Termination and suspension",
        body: (
            <>
                <p>
                    You may close your account at any time from <em>Settings → Account</em>. We may suspend or terminate your
                    access to all or part of the Service, with or without notice, if (a) you violate these Terms, our Community
                    Guidelines, or applicable law; (b) we are required to do so by law or court order; or (c) the operation of
                    the Service is no longer commercially viable.
                </p>
                <p>
                    Sections that by their nature should survive termination &mdash; including license grants for content already
                    distributed, intellectual-property ownership, disclaimers, limitations of liability, and dispute resolution
                    &mdash; will survive.
                </p>
            </>
        ),
    },
    {
        id: "disclaimer",
        title: "Disclaimers",
        body: (
            <>
                <p>
                    THE SERVICE IS PROVIDED <strong>&ldquo;AS IS&rdquo;</strong> AND <strong>&ldquo;AS AVAILABLE&rdquo;</strong>{" "}
                    WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING ANY WARRANTIES OF MERCHANTABILITY, FITNESS FOR
                    A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
                </p>
                <p>
                    WE DO NOT WARRANT THE ACCURACY, COMPLETENESS, OR USEFULNESS OF ANY USER-GENERATED CONTENT, GPS ROUTE,
                    DIFFICULTY RATING, MAP, REVIEW, OR DESCRIPTION ON THE SERVICE.
                </p>
                <p>
                    Some jurisdictions do not allow the disclaimer of certain implied warranties; in those jurisdictions some of
                    the above disclaimers may not apply to you, but our liability is limited to the maximum extent permitted by
                    law.
                </p>
            </>
        ),
    },
    {
        id: "limitation",
        title: "Limitation of liability",
        body: (
            <>
                <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, {E.toUpperCase()} AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AGENTS,
                    AND LICENSORS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE
                    DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING FROM OR
                    RELATED TO YOUR USE OF THE SERVICE.
                </p>
                <p>
                    OUR TOTAL CUMULATIVE LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF (a) THE
                    AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (b) USD $100.
                </p>
                <p>
                    These limitations apply even if a remedy fails of its essential purpose. Some jurisdictions do not allow the
                    exclusion or limitation of incidental or consequential damages, so the above may not apply to you in full.
                </p>
            </>
        ),
    },
    {
        id: "indemnity",
        title: "Indemnification",
        body: (
            <>
                <p>
                    To the extent permitted by law, you agree to indemnify, defend, and hold harmless {E} and its affiliates from
                    any claim, demand, loss, or damages, including reasonable attorneys&rsquo; fees, arising from or related to
                    (a) your use or misuse of the Service, (b) Your Content, (c) your violation of these Terms, or (d) your
                    violation of any law or third-party right.
                </p>
            </>
        ),
    },
    {
        id: "governing-law",
        title: "Governing law and dispute resolution",
        body: (
            <>
                <p>
                    These Terms and any dispute arising out of or related to them or the Service are governed by the laws of the{" "}
                    <strong>{BRAND.legal.jurisdiction}</strong>, without regard to conflict-of-laws principles. The United Nations
                    Convention on Contracts for the International Sale of Goods does not apply.
                </p>
                <p>
                    You and {E} agree to bring any claim only in <strong>{BRAND.legal.venue}</strong> and consent to personal
                    jurisdiction there. To the fullest extent permitted by law, you and {E} waive any right to a jury trial and
                    agree that any claim must be brought in an individual capacity and not as a plaintiff or class member in any
                    class or representative proceeding.
                </p>
                <p>
                    Nothing in this section limits your statutory rights as a consumer in your country of residence. EU and UK
                    consumers retain the right to bring claims in their country of habitual residence.
                </p>
            </>
        ),
    },
    {
        id: "changes",
        title: "Changes to these Terms",
        body: (
            <>
                <p>
                    We may modify these Terms from time to time. If a change is material, we will notify you by email or via an
                    in-app notice at least 14 days before it takes effect. Continued use of the Service after the effective date
                    constitutes acceptance of the updated Terms. If you do not accept the change, you must stop using the Service
                    and may close your account.
                </p>
            </>
        ),
    },
    {
        id: "misc",
        title: "Miscellaneous",
        body: (
            <>
                <p>
                    These Terms, together with the <a href="/privacy">Privacy Policy</a> and{" "}
                    <a href="/community-guidelines">Community Guidelines</a>, are the entire agreement between you and {E}{" "}
                    regarding the Service and supersede prior agreements. If any provision is held unenforceable, the remaining
                    provisions remain in full force. Our failure to enforce a right is not a waiver. You may not assign these
                    Terms; we may assign them in connection with a merger, acquisition, or sale of assets.
                </p>
            </>
        ),
    },
    {
        id: "contact",
        title: "Contact us",
        body: (
            <>
                <p>
                    Questions about these Terms? Reach us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> or by mail at:
                </p>
                <p>
                    <strong>{E}</strong><br />
                    {BRAND.legal.mailingAddress}
                </p>
            </>
        ),
    },
];

export default function Terms() {
    return (
        <LegalLayout
            testid="terms-page"
            title="Terms of Service"
            lede={`These Terms govern your use of ${APP}. They include important information about subscriptions, the inherent risk of outdoor activities, intellectual property, and dispute resolution. Please read them carefully.`}
            sections={SECTIONS}
        />
    );
}
